import { useCallback, useEffect, useRef, useState } from 'react';

import { di } from 'react-magnetic-di';
import { useDebouncedCallback } from 'use-debounce';

import type { OptionsType, OptionType } from '@atlaskit/select';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { performGqlFetch } from '@atlassian/conversation-assistant-utils/graphqlUtils';

const DEBOUNCE_DELAY = 300;

const SearchJiraProjectsQuery = `
query GetProjects($query: String!, $siteAri: String!) {
  search {
		search(
			query: $query
			filters: {
				entities: ["ati:cloud:jira:project"],
				locations: [$siteAri],
			}
			experience: "rovo-chat"
		) {
			edges {
				node {
					... on SearchResultJiraProject {
						id
						projectKey
						title
						iconUrl
						url
						project {
							issueTypes {
								edges {
									node {
										issueTypeId
										name
										avatar {
											xsmall
										}
									}
								}
							}
						}
					}
				}
			}
		}
	}
}
`;

type SearchJiraProjectsResponse = {
	search: {
		search: {
			edges: {
				node: {
					id: string;
					projectKey: string;
					title: string;
					iconUrl: string;
					url: string;
					project: {
						issueTypes: {
							edges: {
								node: {
									issueTypeId: string;
									name: string;
									avatar: {
										xsmall: string;
									};
								};
							}[];
						};
					};
				};
			}[];
		};
	};
};

export type ProjectIssueType = {
	issueTypeId: string;
	name: string;
	avatarUrl: string;
};

/** OptionType extended with the per-project metadata returned by the search query. */
export type ProjectOption = OptionType & {
	projectKey?: string;
	iconUrl?: string;
	issueTypes?: ProjectIssueType[];
};

const extractProjectFromARI = (input: string): string => {
	// Match the last numeric segment after "project/"
	const pattern = /project\/(\d+)$/;
	const match = input.match(pattern);
	return match ? match[1] : '';
};

// Read iconUrl off an option without forcing every consumer to cast the loose OptionType.
export const getOptionIconUrl = (option: OptionType): string | undefined =>
	(option as ProjectOption).iconUrl;

type UseProjectSearchArgs = {
	siteId: string;
	isDisabled?: boolean;
	selectedProject?: OptionType;
	setProject: (args: {
		projectId: string;
		projectKey: string;
		projectName: string;
		issueTypes: ProjectIssueType[];
	}) => void;
};

type UseProjectSearchResult = {
	displayedOptions: OptionsType;
	isLoading: boolean;
	sortOptionsWithSelectedFirst: (options: OptionsType) => OptionsType;
	handleProjectChange: (option: OptionType | null) => void;
	debouncedLoadOptions: (value: string) => void;
	handleMenuOpen: () => void;
	handleMenuClose: () => void;
};

/**
 * Shared state + handlers for the Jira project pickers.
 *
 * - Preloads an initial set of projects via the AGG search.
 * - Debounces follow-up searches as the user types.
 * - Keeps the previously-displayed options visible while a request is in flight,
 *   so consumers can render an inline (non-blocking) loading indicator.
 */
export const useProjectSearch = ({
	siteId,
	isDisabled = false,
	selectedProject,
	setProject,
}: UseProjectSearchArgs): UseProjectSearchResult => {
	di(performGqlFetch, useAnalytics);

	const { sendAnalyticsEvent } = useAnalytics();
	const [allOptions, setAllOptions] = useState<OptionsType>([]);
	const [displayedOptions, setDisplayedOptions] = useState<OptionsType>([]);
	const [isLoading, setIsLoading] = useState(false);
	const hasFetchedInitialOptions = useRef(false);

	const loadOptions = useCallback(
		async (value: string): Promise<OptionsType> => {
			setIsLoading(true);
			try {
				const response = await performGqlFetch<
					SearchJiraProjectsResponse,
					{ query: string; siteAri: string }
				>({
					gqlQuery: SearchJiraProjectsQuery,
					variables: {
						query: value,
						siteAri: `ari:cloud:jira::site/${siteId}`,
					},
				});

				return (
					response.data?.search?.search?.edges
						?.map((edge) => edge.node)
						?.map<ProjectOption>((item) => ({
							projectKey: item.projectKey,
							label: item.title,
							value: item.id,
							iconUrl: item.iconUrl,
							issueTypes: item.project.issueTypes.edges.map(({ node }) => ({
								issueTypeId: node.issueTypeId,
								name: node.name,
								avatarUrl: node.avatar?.xsmall,
							})),
						})) ?? []
				);
			} catch (error) {
				logException({
					exception: error,
					name: 'SearchJiraProjectsQueryError',
					tags: { cloudId: siteId },
				});
				return [];
			} finally {
				hasFetchedInitialOptions.current = true;
				setIsLoading(false);
			}
		},
		[siteId],
	);

	const sortOptionsWithSelectedFirst = useCallback(
		(options: OptionsType): OptionsType => {
			if (!selectedProject?.value || !options || options.length === 0) {
				return options || [];
			}

			const selectedId =
				extractProjectFromARI(String(selectedProject.value)) || String(selectedProject.value);

			const otherOptions = options.filter((option) => {
				const optionId =
					typeof option.value === 'string'
						? extractProjectFromARI(option.value) || option.value
						: option.value;
				return optionId !== selectedId;
			});

			return [selectedProject, ...otherOptions];
		},
		[selectedProject],
	);

	useEffect(() => {
		const preloadOptions = async () => {
			if (
				allOptions.length === 0 &&
				!isLoading &&
				!isDisabled &&
				!hasFetchedInitialOptions.current
			) {
				const initialOptions = await loadOptions('');
				setAllOptions(initialOptions);
				setDisplayedOptions(initialOptions);
			}
		};

		preloadOptions();
	}, [allOptions.length, isDisabled, isLoading, loadOptions]);

	const handleProjectChange = useCallback(
		(selectedOption: ProjectOption | null) => {
			const selectedOptionValue = selectedOption?.value;
			if (!selectedOption || typeof selectedOptionValue !== 'string') {
				return;
			}

			const selectedOptionProjectId =
				extractProjectFromARI(selectedOptionValue) || selectedOptionValue;
			if (selectedOptionProjectId === selectedProject?.value) {
				return;
			}

			setProject({
				projectId: selectedOptionProjectId,
				projectKey: selectedOption.projectKey ?? '',
				projectName: selectedOption.label ?? '',
				issueTypes: (selectedOption as ProjectOption).issueTypes ?? [],
			});

			if (!allOptions.some((option) => option.value === selectedOptionValue)) {
				setAllOptions((prev) => [...prev, selectedOption]);
			}
			setDisplayedOptions(allOptions);
		},
		[selectedProject, setProject, allOptions],
	);

	const [debouncedLoadOptions] = useDebouncedCallback(async (value: string) => {
		setDisplayedOptions(await loadOptions(value));
	}, DEBOUNCE_DELAY);

	const handleMenuOpen = useCallback(() => {
		sendAnalyticsEvent({
			action: 'opened',
			actionSubject: 'dropdown',
			actionSubjectId: 'jiraAifcPreviewCardProjectSelector',
		});
	}, [sendAnalyticsEvent]);

	const handleMenuClose = useCallback(() => {
		setDisplayedOptions(allOptions);
	}, [allOptions]);

	return {
		displayedOptions,
		isLoading,
		sortOptionsWithSelectedFirst,
		handleProjectChange,
		debouncedLoadOptions,
		handleMenuOpen,
		handleMenuClose,
	};
};
